// src/services/proxyService.js
// Servicios que usan el proxy /api/db en vez de supabase directo
// Importa esto en vez de supabaseService donde quieras usar el proxy
import { supabase } from '../config/supabase';
import db from './dbProxy';
import { storageProxy } from './storageProxy';

// ============================================================
// CLUB SERVICE
// ============================================================
export const clubServiceProxy = {
  async getById(clubId) {
    console.log('[ClubDashboard] Loading club:', clubId);
    try {
      const { data, error } = await db('clubs')
        .select('*')
        .eq('id', clubId)
        .single()
        .execute();
      
      console.log('[ClubDashboard] Club result:', { data, error });
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[ClubDashboard] Error:', err.message);
      return { data: null, error: err.message };
    }
  },

  async update(id, updates) {
    try {
      const { data, error } = await db('clubs')
        .update(updates)
        .eq('id', id)
        .execute();
      
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en clubServiceProxy.update:', err.message);
      return { data: null, error: err.message };
    }
  },
};

// ============================================================
// EVENT SERVICE (proxied)
// ============================================================
export const eventServiceProxy = {
  async getAll() {
    try {
      const { data, error } = await db('events')
        .select('*')
        .order('date', { ascending: false })
        .execute();
      
      if (error) throw new Error(error);
      return { data: data || [], error: null };
    } catch (err) {
      console.error('[proxyService] Error en eventServiceProxy.getAll:', err.message);
      return { data: [], error: err.message };
    }
  },

  async getById(eventId) {
    try {
      const { data, error } = await db('events')
        .select('*')
        .eq('id', eventId)
        .single()
        .execute();
      
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en eventServiceProxy.getById:', err.message);
      return { data: null, error: err.message };
    }
  },
};

// ============================================================
// EVENT_CLUBS SERVICE (proxied)
// ============================================================
export const eventClubServiceProxy = {
  async getByClub(clubId) {
    try {
      const { data, error } = await db('event_clubs')
        .select('event_id, status')
        .eq('club_id', clubId)
        .execute();
      
      if (error) throw new Error(error);
      return { data: data || [], error: null };
    } catch (err) {
      console.error('[proxyService] Error en eventClubServiceProxy.getByClub:', err.message);
      return { data: [], error: err.message };
    }
  },

  async getAllRequests(clubId) {
    try {
      const { data, error } = await db('event_clubs')
        .select('event_id, status, created_at')
        .eq('club_id', clubId)
        .order('created_at', { ascending: false })
        .execute();
      
      if (error) throw new Error(error);
      return { data: data || [], error: null };
    } catch (err) {
      console.error('[proxyService] Error en eventClubServiceProxy.getAllRequests:', err.message);
      return { data: [], error: err.message };
    }
  },

  async requestAccess(clubId, eventId, accessCode) {
    try {
      const { data, error } = await db('event_clubs')
        .insert({
          club_id: clubId,
          event_id: eventId,
          status: 'pending',
          access_code: accessCode,
        })
        .execute();
      
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en eventClubServiceProxy.requestAccess:', err.message);
      return { data: null, error: err.message };
    }
  },
};

// ============================================================
// GYMNAST SERVICE (proxied)
// ============================================================
export const gymnastServiceProxy = {
  async getByClub(clubId) {
    try {
      const { data, error } = await db('gimnasts')
        .select('*')
        .eq('club_id', clubId)
        .order('name', { ascending: true })
        .execute();
      
      if (error) throw new Error(error);
      return { data: data || [], error: null };
    } catch (err) {
      console.error('[proxyService] Error en gymnastServiceProxy.getByClub:', err.message);
      return { data: [], error: err.message };
    }
  },

  async create(gymnast) {
    try {
      const { data, error } = await db('gimnasts')
        .insert(gymnast)
        .execute();
      
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en gymnastServiceProxy.create:', err.message);
      return { data: null, error: err.message };
    }
  },

  async update(id, updates) {
    try {
      const { data, error } = await db('gimnasts')
        .update(updates)
        .eq('id', id)
        .execute();
      
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en gymnastServiceProxy.update:', err.message);
      return { data: null, error: err.message };
    }
  },

  async delete(id) {
    try {
      const { data, error } = await db('gimnasts')
        .delete()
        .eq('id', id)
        .execute();
      
      if (error) throw new Error(error);
      return { error: null };
    } catch (err) {
      console.error('[proxyService] Error en gymnastServiceProxy.delete:', err.message);
      return { error: err.message };
    }
  },
};

// ============================================================
  // IMAGES SERVICE (proxied)
  // ============================================================
  export const imageServiceProxy = {
    async getByEntity(entityType, entityId) {
      try {
        const { data, error } = await db('images')
          .select('image_url')
          .eq('entity_type', entityType)
          .eq('entity_id', String(entityId))
          .order('created_at', { ascending: false })
          .limit(1)
          .execute();
        
        if (error) throw new Error(error);
        return { data: data?.[0]?.image_url || null, error: null };
      } catch (err) {
        console.error(`[proxyService] Error obteniendo imagen para ${entityType} ${entityId}:`, err.message);
        return { data: null, error: err.message };
      }
    },

    async setImage(entityType, entityId, imageUrl, filePath) {
      try {
        // Eliminar imagen existente
        const deleteResult = await db('images')
          .delete()
          .eq('entity_type', entityType)
          .eq('entity_id', String(entityId))
          .execute();
        console.log('[proxyService] Delete image result:', deleteResult);
        
        // Insertar nueva imagen
        const insertResult = await db('images')
          .insert([{
            entity_type: entityType,
            entity_id: String(entityId),
            image_url: imageUrl,
            file_path: filePath || imageUrl
          }])
          .execute();
        
        console.log('[proxyService] Insert image result:', insertResult);
        if (insertResult.error) throw new Error(insertResult.error);

        // Sincronizar URL a la columna específica de la entidad
        // (photo_url para gimnasts/coaches/judges, image_url para events/clubs).
        // Para coaches, entity_id puede ser coaches.id o auth_user_id -> intentamos ambos.
        try {
          const tableFieldMap = {
            gymnast: { table: 'gimnasts', field: 'photo_url', alsoMatchField: null },
            coach:   { table: 'coaches',  field: 'photo_url', alsoMatchField: 'auth_user_id' },
            judge:   { table: 'judges',   field: 'photo_url', alsoMatchField: null },
            event:   { table: 'events',   field: 'image_url', alsoMatchField: null },
            // clubs: no tiene columna image_url en el schema, se lee siempre desde tabla images
          };
          const map = tableFieldMap[entityType];
          if (map) {
            const upd1 = await db(map.table)
              .update({ [map.field]: imageUrl })
              .eq('id', entityId)
              .execute();
            // Si no matcheó por id y hay columna alternativa (coaches.auth_user_id), reintentamos
            if (map.alsoMatchField && (!upd1.data || (Array.isArray(upd1.data) && upd1.data.length === 0))) {
              await db(map.table)
                .update({ [map.field]: imageUrl })
                .eq(map.alsoMatchField, entityId)
                .execute();
            }
          }
        } catch (syncErr) {
          console.warn(`[proxyService] No se pudo sincronizar ${entityType}.photo_url:`, syncErr.message);
        }

        return { data: insertResult.data, error: null };
      } catch (err) {
        console.error(`[proxyService] Error guardando imagen para ${entityType} ${entityId}:`, err.message);
        return { data: null, error: err.message };
      }
    },
  };

// ============================================================
// COACH_CLUB_ACCESS SERVICE (proxied)
// ============================================================
export const coachClubAccessServiceProxy = {
  async getRequestsByClub(clubId) {
    try {
      const { data, error } = await db('coach_club_access')
        .select('*')
        .eq('club_id', clubId)
        .order('requested_at', { ascending: false })
        .execute();
      
      if (error) throw new Error(error);
      return { data: data || [], error: null };
    } catch (err) {
      console.error('[proxyService] Error en coachClubAccessServiceProxy.getRequestsByClub:', err.message);
      return { data: [], error: err.message };
    }
  },

  async approveRequest(accessId) {
    try {
      const { data, error } = await db('coach_club_access')
        .update({ status: 'approved', resolved_at: new Date().toISOString() })
        .eq('id', accessId)
        .execute();
      
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en coachClubAccessServiceProxy.approveRequest:', err.message);
      return { data: null, error: err.message };
    }
  },

  async getByCoachAndClub(coachId, clubId) {
    try {
      const { data, error } = await db('coach_club_access')
        .select('id, status')
        .eq('coach_id', coachId)
        .eq('club_id', clubId)
        .single()
        .execute();
      
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en coachClubAccessServiceProxy.getByCoachAndClub:', err.message);
      return { data: null, error: err.message };
    }
  },

  async count(clubId, status) {
    try {
      let query = db('coach_club_access')
        .select('id', { count: 'exact', head: true })
        .eq('club_id', clubId);
      
      if (status) query = query.eq('status', status);

      const { count, error } = await query.execute();
      if (error) throw new Error(error);
      return { count: count || 0, error: null };
    } catch (err) {
      console.error('[proxyService] Error en coachClubAccessServiceProxy.count:', err.message);
      return { count: 0, error: err.message };
    }
  },
};

// ============================================================
// COACHES SERVICE (proxied)
// ============================================================
export const coachServiceProxy = {
  async getById(coachId) {
    try {
      const { data, error } = await db('coaches')
        .select('*')
        .eq('id', coachId)
        .single()
        .execute();
      
      if (error) throw new Error(error);
      
      // Load photo from images table
      if (data) {
        try {
          const { data: imgData } = await db('images')
            .select('image_url')
            .eq('entity_type', 'coach')
            .eq('entity_id', coachId)
            .order('created_at', { ascending: false })
            .limit(1)
            .execute();
          if (imgData && imgData.length > 0) {
            data.photo_url = imgData[0].image_url;
          }
        } catch (e) { /* ignore photo load error */ }
      }
      
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en coachServiceProxy.getById:', err.message);
      return { data: null, error: err.message };
    }
  },
};

// ============================================================
  // ORGANIZERS SERVICE (proxied)
  // ============================================================
  export const organizerServiceProxy = {
    async getById(organizerId) {
      try {
        const { data, error } = await db('organizers')
          .select('*')
          .eq('id', organizerId)
          .single()
          .execute();
        
        if (error) throw new Error(error);
        return { data, error: null };
      } catch (err) {
        console.error('[proxyService] Error en organizerServiceProxy.getById:', err.message);
        return { data: null, error: err.message };
      }
    },

async update(organizerId, updates) {
      try {
        const { data, error } = await db('organizers')
          .update(updates)
          .eq('id', organizerId)
          .execute();
        
        if (error) throw new Error(error);
        return { data, error: null };
      } catch (err) {
        console.error('[proxyService] Error en organizerServiceProxy.update:', err.message);
        return { data: null, error: err.message };
      }
    },
  };

// ============================================================
// EVENT_LEVELS SERVICE (proxied)
// ============================================================
export const eventLevelServiceProxy = {
  async deleteByEvent(eventId) {
    try {
      const { error } = await db('event_levels')
        .delete()
        .eq('event_id', eventId)
        .execute();
      
      if (error) throw new Error(error);
      return { error: null };
    } catch (err) {
      console.error('[proxyService] Error en eventLevelServiceProxy.deleteByEvent:', err.message);
      return { error: err.message };
    }
  },
};

// ============================================================
// GIMNASTS EXTENDED (count, by IDs)
// ============================================================
gymnastServiceProxy.getByIds = async function(clubIds) {
  try {
    const { data, error } = await db('gimnasts')
      .select('*')
      .in('club_id', clubIds)
      .execute();
    
    if (error) throw new Error(error);
    return { data: data || [], error: null };
  } catch (err) {
    console.error('[proxyService] Error en gymnastServiceProxy.getByIds:', err.message);
    return { data: [], error: err.message };
  }
};

gymnastServiceProxy.count = async function(clubId) {
  try {
    const { count, error } = await db('gimnasts')
      .select('id', { count: 'exact', head: true })
      .eq('club_id', clubId)
      .execute();
    
    if (error) throw new Error(error);
    return { count: count || 0, error: null };
  } catch (err) {
    console.error('[proxyService] Error en gymnastServiceProxy.count:', err.message);
    return { count: 0, error: err.message };
  }
};

// ============================================================
// EVENT_CLUBS EXTENDED (getByEvent, delete)
// ============================================================
eventClubServiceProxy.getByEvent = async function(eventId) {
  try {
    const { data, error } = await db('event_clubs')
      .select('*, clubs:club_id(id, name, email, location)')
      .eq('event_id', eventId)
      .execute();
    
    if (error) throw new Error(error);
    return { data: data || [], error: null };
  } catch (err) {
    console.error('[proxyService] Error en eventClubServiceProxy.getByEvent:', err.message);
    return { data: [], error: err.message };
  }
};

eventClubServiceProxy.deleteById = async function(requestId) {
  try {
    const { error } = await db('event_clubs')
      .delete()
      .eq('id', requestId)
      .execute();
    
    if (error) throw new Error(error);
    return { error: null };
  } catch (err) {
    console.error('[proxyService] Error en eventClubServiceProxy.deleteById:', err.message);
    return { error: err.message };
  }
};

// ============================================================
// EVENTOS SERVICE EXTENDED
// ============================================================
eventServiceProxy.getAll = async function() {
  try {
    const { data, error } = await db('events')
      .select('*')
      .order('date', { ascending: false })
      .execute();
    
    if (error) throw new Error(error);

    // Enriquecer con imágenes
    const enrichedEvents = await Promise.all((data || []).map(async (event) => {
      const { data: imgUrl } = await imageServiceProxy.getByEntity('event', event.id);
      return { ...event, image_url: event.image_url || imgUrl };
    }));

    return { data: enrichedEvents, error: null };
  } catch (err) {
    console.error('[proxyService] Error en eventServiceProxy.getAll:', err.message);
    return { data: [], error: err.message };
  }
};

// ============================================================
// EVENT ACCESS SERVICE (proxied) - Códigos de acceso
// ============================================================
export const eventAccessServiceProxy = {
  async validateCode(code) {
    try {
      // Nota: event_codes no event_access_codes
      const { data, error } = await db('event_codes')
        .select('*, events:event_id(id, name)')
        .eq('code', code.toUpperCase().trim())
        .eq('is_active', true)
        .single()
        .execute();
      
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error validando código:', err.message);
      return { data: null, error: err.message };
    }
  },

  async requestEventAccess(clubId, eventId, code) {
    try {
      // Check if already requested
      const { data: existing } = await db('event_clubs')
        .select('id, status')
        .eq('club_id', clubId)
        .eq('event_id', eventId)
        .single()
        .execute();

      if (existing) {
        if (existing.status === 'approved') return { error: 'Ya tienes acceso a esta competencia' };
        if (existing.status === 'pending') return { error: 'Ya tienes una solicitud pendiente para esta competencia' };
        
        // If rejected, allow re-request by updating
        const { error: updateError } = await db('event_clubs')
          .update({ status: 'pending' })
          .eq('id', existing.id)
          .execute();
        
        if (updateError) throw new Error(updateError);
        return { error: null };
      }

      const { error } = await db('event_clubs')
        .insert({ club_id: clubId, event_id: eventId, status: 'pending' })
        .execute();
      
      if (error) throw new Error(error);
      return { error: null };
    } catch (err) {
      console.error('[proxyService] Error solicitando acceso:', err.message);
      return { error: err.message };
    }
  },

  async getEventCode(eventId) {
    try {
      const { data, error } = await db('event_codes')
        .select('*')
        .eq('event_id', eventId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .single()
        .execute();
      
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error obteniendo código:', err.message);
      return { data: null, error: err.message };
    }
  },

  async generateEventCode(eventId) {
    try {
      // Generate a random 8-char code
      const code = Math.random().toString(36).substring(2, 10).toUpperCase();

      // Deactivate any existing codes for this event
      await db('event_codes')
        .update({ is_active: false })
        .eq('event_id', eventId)
        .execute();

      const { data, error } = await db('event_codes')
        .insert({ event_id: eventId, code, is_active: true })
        .execute();

      if (error) throw new Error(error);
      return { data: data?.[0], error: null };
    } catch (err) {
      console.error('[proxyService] Error generando código:', err.message);
      return { data: null, error: err.message };
    }
  },
};

// ============================================================
// COACH_CLUB_ACCESS SERVICE EXTENDED
// ============================================================
coachClubAccessServiceProxy.getApprovedCoaches = async function(clubId) {
  try {
    const { data, error } = await db('coach_club_access')
      .select('id, coach_id, role, club_id, status, pose_analysis_enabled, coaches(id, name, email, phone, specialties, experience_years, bio, cv_url, location, photo_url, auth_user_id)')
      .eq('club_id', clubId)
      .eq('status', 'approved')
      .execute();

    if (error) throw new Error(error);

    // Fallback: si no hay photo_url en la columna, buscar en tabla images.
    // Entity_id puede ser coaches.id o auth_user_id (según qué UI subió la foto).
    if (data && data.length > 0) {
      for (const item of data) {
        const coach = item.coaches;
        if (!coach || coach.photo_url) continue; // ya tiene foto

        const candidateIds = [coach.id, coach.auth_user_id].filter(Boolean);
        for (const entId of candidateIds) {
          try {
            const { data: imgData } = await db('images')
              .select('image_url')
              .eq('entity_type', 'coach')
              .eq('entity_id', entId)
              .order('created_at', { ascending: false })
              .limit(1)
              .execute();
            if (imgData && imgData.length > 0) {
              coach.photo_url = imgData[0].image_url;
              break;
            }
          } catch (e) { /* ignore */ }
        }
      }
    }

    return { data: data || [], error: null };
  } catch (err) {
    console.error('[proxyService] Error en getApprovedCoaches:', err.message);
    return { data: [], error: err.message };
  }
};

// Habilitar/deshabilitar Análisis IA para un entrenador específico dentro
// del club. Requiere que el club además tenga clubs.pose_analysis_enabled
// (eso es el "switch maestro" y lo valida la UI/CoachDashboard).
coachClubAccessServiceProxy.setCoachPoseAnalysis = async function(accessId, enabled) {
  try {
    const { data, error } = await db('coach_club_access')
      .update({ pose_analysis_enabled: !!enabled })
      .eq('id', accessId)
      .execute();
    if (error) throw new Error(error);
    return { data, error: null };
  } catch (err) {
    console.error('[proxyService] Error en setCoachPoseAnalysis:', err.message);
    return { data: null, error: err.message };
  }
};

coachClubAccessServiceProxy.rejectRequest = async function(accessId) {
  try {
    const { data, error } = await db('coach_club_access')
      .update({ status: 'rejected', resolved_at: new Date().toISOString() })
      .eq('id', accessId)
      .execute();
    
    if (error) throw new Error(error);
    return { data, error: null };
  } catch (err) {
    console.error('[proxyService] Error en rejectRequest:', err.message);
    return { data: null, error: err.message };
  }
};

coachClubAccessServiceProxy.removeCoach = async function(accessId) {
  try {
    const { error } = await db('coach_club_access')
      .delete()
      .eq('id', accessId)
      .execute();
    
    if (error) throw new Error(error);
    return { error: null };
  } catch (err) {
    console.error('[proxyService] Error en removeCoach:', err.message);
    return { error: err.message };
  }
};

coachClubAccessServiceProxy.getCoachesLookingForClub = async function() {
  try {
    const { data, error } = await db('coaches')
      .select('id, name, email, phone, location, bio, specialties, experience_years, photo_url, cv_url, looking_for_club, auth_user_id')
      .eq('looking_for_club', true)
      .order('name', { ascending: true })
      .execute();
    
    if (error) throw new Error(error);
    
    // Load photos from images table for each coach
    if (data && data.length > 0) {
      for (const coach of data) {
        try {
          // If coach already has photo_url, use it
          if (coach.photo_url) {
            continue;
          }
          
          // Try with coach id
          let { data: imgData1 } = await db('images')
            .select('image_url')
            .eq('entity_type', 'coach')
            .eq('entity_id', coach.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .execute();
          
          if (imgData1?.[0]?.image_url) {
            coach.photo_url = imgData1[0].image_url;
          } else if (coach.auth_user_id) {
            // Try with auth_user_id (como se guarda en CoachDashboard)
            const { data: imgData2 } = await db('images')
              .select('image_url')
              .eq('entity_type', 'coach')
              .eq('entity_id', coach.auth_user_id)
              .order('created_at', { ascending: false })
              .limit(1)
              .execute();
            
            if (imgData2?.[0]?.image_url) {
              coach.photo_url = imgData2[0].image_url;
            }
          }
        } catch (imgErr) {
          console.log('[CoachDashboard] No image for coach:', coach.id, imgErr.message);
        }
      }
    }
    
    return { data: data || [], error: null };
  } catch (err) {
    console.error('[proxyService] Error en getCoachesLookingForClub:', err.message);
    return { data: [], error: err.message };
  }
};

coachClubAccessServiceProxy.requestContact = async function(clubId, coachId) {
  try {
    // Verificar si ya existe contacto
    const { data: existing } = await db('club_coach_contacts')
      .select('*')
      .eq('club_id', clubId)
      .eq('coach_id', coachId)
      .execute();
    
    if (existing && existing.length > 0) {
      // Ya existe, retornar el existente
      return { data: existing[0], error: null };
    }
    
    const { data, error } = await db('club_coach_contacts')
      .insert({
        club_id: clubId,
        coach_id: coachId,
        status: 'pending',
        requested_at: new Date().toISOString()
      })
      .select()
      .execute();
    
    if (error) throw new Error(error);
    return { data: data?.[0] || null, error: null };
  } catch (err) {
    console.error('[proxyService] Error en requestContact:', err.message);
    return { data: null, error: err.message };
  }
};

coachClubAccessServiceProxy.getContactStatus = async function(clubId, coachId) {
  try {
    const { data, error } = await db('club_coach_contacts')
      .select('*')
      .eq('club_id', clubId)
      .eq('coach_id', coachId)
      .order('requested_at', { ascending: false })
      .limit(1)
      .execute();
    
    if (error) throw new Error(error);
    return { data: data?.[0] || null, error: null };
  } catch (err) {
    console.error('[proxyService] Error en getContactStatus:', err.message);
    return { data: null, error: null };
  }
};

coachClubAccessServiceProxy.getClubContactsForCoach = async function(coachId) {
  try {
    const { data, error } = await db('club_coach_contacts')
      .select('*, clubs:club_id(id, name, location, phone, email, facebook_url, instagram_url, twitter_url)')
      .eq('coach_id', coachId)
      .order('requested_at', { ascending: false })
      .execute();
    
    if (error) throw new Error(error);
    return { data: data || [], error: null };
  } catch (err) {
    console.error('[proxyService] Error en getClubContactsForCoach:', err.message);
    return { data: [], error: err.message };
  }
};

coachClubAccessServiceProxy.confirmContact = async function(contactId) {
  try {
    const { data, error } = await db('club_coach_contacts')
      .update({ status: 'confirmed', confirmed_at: new Date().toISOString() })
      .eq('id', contactId)
      .select()
      .execute();
    
    if (error) throw new Error(error);
    return { data: data?.[0] || null, error: null };
  } catch (err) {
    console.error('[proxyService] Error en confirmContact:', err.message);
    return { data: null, error: err.message };
  }
};

// ============================================================
// JUDGES SERVICE (proxied)
// ============================================================
export const judgeServiceProxy = {
  async getById(judgeId) {
    try {
      const { data, error } = await db('judges')
        .select('*')
        .eq('id', judgeId)
        .single()
        .execute();
      
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en judgeServiceProxy.getById:', err.message);
      return { data: null, error: err.message };
    }
  },

  async getEventsByJudge(judgeId) {
    try {
      const { data, error } = await db('judge_event_assignments')
        .select('*, events:event_id(*)')
        .eq('judge_id', judgeId)
        .execute();
      
      if (error) throw new Error(error);
      return { data: data || [], error: null };
    } catch (err) {
      console.error('[proxyService] Error en judgeServiceProxy.getEventsByJudge:', err.message);
      return { data: [], error: err.message };
    }
  },

  async addToEvent(eventId, judgeId) {
    try {
      const { data, error } = await db('judge_event_assignments')
        .insert({ event_id: eventId, judge_id: judgeId })
        .execute();
      
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en judgeServiceProxy.addToEvent:', err.message);
      return { data: null, error: err.message };
    }
  },
};

// ============================================================
// JUDGE CONFIG SERVICE (proxied)
// ============================================================
export const judgeConfigServiceProxy = {
  async getConfigsByEvent(judgeId, eventId) {
    try {
      const { data, error } = await db('judge_event_configs')
        .select('*')
        .eq('judge_id', judgeId)
        .eq('event_id', eventId)
        .execute();
      
      if (error) throw new Error(error);
      return { data: data || [], error: null };
    } catch (err) {
      console.error('[proxyService] Error en judgeConfigServiceProxy.getConfigsByEvent:', err.message);
      return { data: [], error: err.message };
    }
  },
};

// ============================================================
// JUDGE INVITATION SERVICE (proxied)
// ============================================================
export const judgeInvitationServiceProxy = {
  async validateInvitation(code) {
    try {
      const { data, error } = await db('judge_invitations')
        .select('*')
        .eq('code', code.toUpperCase().trim())
        .eq('is_active', true)
        .eq('used', false)
        .single()
        .execute();
      
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en judgeInvitationServiceProxy.validateInvitation:', err.message);
      return { data: null, error: err.message };
    }
  },

  async markInvitationAsUsed(invitationId, judgeId) {
    try {
      const { data, error } = await db('judge_invitations')
        .update({ used: true, used_by_judge_id: judgeId, used_at: new Date().toISOString() })
        .eq('id', invitationId)
        .execute();
      
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en judgeInvitationServiceProxy.markInvitationAsUsed:', err.message);
      return { data: null, error: err.message };
    }
  },
};

// ============================================================
// CLUB AUTH SERVICE (proxied)
// ============================================================
export const clubAuthServiceProxy = {
  async getByEmail(email) {
    try {
      const { data, error } = await db('clubs')
        .select('*')
        .eq('email', email)
        .single()
        .execute();
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en clubAuthServiceProxy.getByEmail:', err.message);
      return { data: null, error: err.message };
    }
  },

  async updatePassword(clubId, newPassword) {
    try {
      const encoder = new TextEncoder();
      const dataBytes = encoder.encode(newPassword);
      const hashBuffer = await crypto.subtle.digest('SHA-256', dataBytes);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      const { data, error } = await db('clubs')
        .update({ password_hash: hashHex })
        .eq('id', clubId)
        .execute();
      if (error) throw new Error(error);
      return { error: null };
    } catch (err) {
      console.error('[proxyService] Error en clubAuthServiceProxy.updatePassword:', err.message);
      return { error: err.message };
    }
  },

  async getAllEventRequests(clubId) {
    try {
      const { data: ecData, error: ecError } = await db('event_clubs')
        .select('event_id, status, created_at')
        .eq('club_id', clubId)
        .execute();
      if (ecError) throw new Error(ecError);
      if (!ecData || ecData.length === 0) return { data: [], error: null };

      const eventIds = ecData.map(ec => ec.event_id);
      const { data: events, error: evError } = await db('events')
        .select('id, name, date, location, discipline')
        .in('id', eventIds)
        .execute();
      if (evError) throw new Error(evError);

      const result = ecData.map(ec => {
        const event = (events || []).find(e => e.id === ec.event_id);
        return { ...ec, event };
      });

      return { data: result, error: null };
    } catch (err) {
      console.error('[proxyService] Error en clubAuthServiceProxy.getAllEventRequests:', err.message);
      return { data: null, error: err.message };
    }
  },
};

// ============================================================
// SCHEDULE BLOCK SERVICE (proxied)
// ============================================================
export const scheduleBlockServiceProxy = {
  async getByClub(clubId) {
    try {
      if (!clubId || clubId === 'undefined' || clubId === 'null') return { data: [], error: null };
      const { data, error } = await db('schedule_blocks')
        .select('*')
        .eq('club_id', clubId)
        .eq('is_active', true)
        .order('day_of_week')
        .execute();
      if (error) throw new Error(error);
      return { data: data || [], error: null };
    } catch (err) {
      console.error('[proxyService] Error en scheduleBlockServiceProxy.getByClub:', err.message);
      return { data: [], error: err.message };
    }
  },

  async getBlockAthletes(blockId) {
    try {
      const { data, error } = await db('athlete_schedule_assignments')
        .select('*, gimnasts(id, name, level, franja, category, birth_date, photo_url)')
        .eq('schedule_block_id', blockId)
        .execute();
      if (error) throw new Error(error);
      return { data: data || [], error: null };
    } catch (err) {
      console.error('[proxyService] Error en scheduleBlockServiceProxy.getBlockAthletes:', err.message);
      return { data: [], error: err.message };
    }
  },

  async addAthleteToBlock(gymnastId, blockId, clubId) {
    try {
      const { data, error } = await db('athlete_schedule_assignments')
        .upsert({ gymnast_id: gymnastId, schedule_block_id: blockId, club_id: clubId })
        .execute();
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en scheduleBlockServiceProxy.addAthleteToBlock:', err.message);
      return { data: null, error: err.message };
    }
  },

  async removeAthleteFromBlock(gymnastId, blockId) {
    try {
      const { error } = await db('athlete_schedule_assignments')
        .delete()
        .eq('gymnast_id', gymnastId)
        .eq('schedule_block_id', blockId)
        .execute();
      if (error) throw new Error(error);
      return { error: null };
    } catch (err) {
      console.error('[proxyService] Error en scheduleBlockServiceProxy.removeAthleteFromBlock:', err.message);
      return { error: err.message };
    }
  },

  // Obtiene los bloques asignados a una atleta (con days_of_week incluido)
  async getAthleteBlocks(gymnastId) {
    try {
      const { data, error } = await db('athlete_schedule_assignments')
        .select('schedule_block_id, schedule_blocks(id, title, days_of_week, start_time, end_time)')
        .eq('gymnast_id', gymnastId)
        .execute();
      if (error) throw new Error(error);
      return { data: (data || []).map(r => r.schedule_blocks).filter(Boolean), error: null };
    } catch (err) {
      console.error('[proxyService] Error en scheduleBlockServiceProxy.getAthleteBlocks:', err.message);
      return { data: [], error: err.message };
    }
  },

  async create(blockData) {
    try {
      const { data, error } = await db('schedule_blocks')
        .insert(blockData)
        .execute();
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      // Fallback: si 'days_of_week' aún no existe en el schema (SQL no ejecutado),
      // reintentamos sin esa columna para no bloquear al usuario.
      if (err.message && err.message.includes("'days_of_week'") && blockData.days_of_week !== undefined) {
        console.warn('[proxyService] Columna days_of_week no existe aún. Reintentando sin ella. Ejecuta sql/add_days_of_week_to_schedule_blocks.sql en Supabase.');
        const { days_of_week, ...fallback } = blockData;
        try {
          const { data, error } = await db('schedule_blocks').insert(fallback).execute();
          if (error) throw new Error(error);
          return { data, error: null };
        } catch (err2) {
          console.error('[proxyService] Error en scheduleBlockServiceProxy.create (fallback):', err2.message);
          return { data: null, error: err2.message };
        }
      }
      console.error('[proxyService] Error en scheduleBlockServiceProxy.create:', err.message);
      return { data: null, error: err.message };
    }
  },

  async update(blockId, updateData) {
    try {
      const { data, error } = await db('schedule_blocks')
        .update(updateData)
        .eq('id', blockId)
        .execute();
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      // Fallback igual que en create
      if (err.message && err.message.includes("'days_of_week'") && updateData.days_of_week !== undefined) {
        console.warn('[proxyService] Columna days_of_week no existe aún. Reintentando sin ella. Ejecuta sql/add_days_of_week_to_schedule_blocks.sql en Supabase.');
        const { days_of_week, ...fallback } = updateData;
        try {
          const { data, error } = await db('schedule_blocks').update(fallback).eq('id', blockId).execute();
          if (error) throw new Error(error);
          return { data, error: null };
        } catch (err2) {
          console.error('[proxyService] Error en scheduleBlockServiceProxy.update (fallback):', err2.message);
          return { data: null, error: err2.message };
        }
      }
      console.error('[proxyService] Error en scheduleBlockServiceProxy.update:', err.message);
      return { data: null, error: err.message };
    }
  },

  async delete(blockId) {
    try {
      const { data, error } = await db('schedule_blocks')
        .update({ is_active: false })
        .eq('id', blockId)
        .execute();
      if (error) throw new Error(error);
      return { error: null };
    } catch (err) {
      console.error('[proxyService] Error en scheduleBlockServiceProxy.delete:', err.message);
      return { error: err.message };
    }
  },

  async getBlockCoaches(blockId) {
    try {
      const { data, error } = await db('coach_schedule_assignments')
        .select('*, coaches(id, name, email, phone, specialties, photo_url)')
        .eq('schedule_block_id', blockId)
        .execute();
      if (error) throw new Error(error);
      return { data: data || [], error: null };
    } catch (err) {
      // Tabla no creada aún: devolver vacío silenciosamente
      if (err.message && err.message.includes('coach_schedule_assignments')) {
        console.warn('[proxyService] Tabla coach_schedule_assignments no existe aún. Ejecuta sql/create_coach_schedule_assignments.sql en Supabase.');
        return { data: [], error: null };
      }
      console.error('[proxyService] Error en scheduleBlockServiceProxy.getBlockCoaches:', err.message);
      return { data: [], error: err.message };
    }
  },

  async addCoachToBlock(coachId, blockId, clubId) {
    try {
      const { data, error } = await db('coach_schedule_assignments')
        .upsert({ coach_id: coachId, schedule_block_id: blockId, club_id: clubId })
        .execute();
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en scheduleBlockServiceProxy.addCoachToBlock:', err.message);
      return { data: null, error: err.message };
    }
  },

  async removeCoachFromBlock(coachId, blockId) {
    try {
      const { error } = await db('coach_schedule_assignments')
        .delete()
        .eq('coach_id', coachId)
        .eq('schedule_block_id', blockId)
        .execute();
      if (error) throw new Error(error);
      return { error: null };
    } catch (err) {
      console.error('[proxyService] Error en scheduleBlockServiceProxy.removeCoachFromBlock:', err.message);
      return { error: err.message };
    }
  },
};

// ============================================================
// ATTENDANCE SERVICE (proxied)
// ============================================================
export const attendanceServiceProxy = {
  async markAthleteAttendance(records) {
    try {
      // onConflict: actualizar registro existente en vez de crear duplicado
      const { data, error } = await db('athlete_attendance')
        .upsert(records, { onConflict: 'gymnast_id,schedule_block_id,date' })
        .execute();
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en attendanceServiceProxy.markAthleteAttendance:', err.message);
      return { data: null, error: err.message };
    }
  },

  async getAthleteAttendance(clubId, dateFrom, dateTo) {
    try {
      let query = db('athlete_attendance')
        .select('*, gimnasts(id, name, category, level)')
        .eq('club_id', clubId);
      if (dateFrom) query = query.gte('date', dateFrom);
      if (dateTo) query = query.lte('date', dateTo);
      const { data, error } = await query.order('date', { ascending: false }).execute();
      if (error) throw new Error(error);
      return { data: data || [], error: null };
    } catch (err) {
      console.error('[proxyService] Error en attendanceServiceProxy.getAthleteAttendance:', err.message);
      return { data: [], error: err.message };
    }
  },

  async getAthleteAttendanceByMonth(clubId, year, month) {
    try {
      const dateFrom = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const dateTo = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
      const { data, error } = await db('athlete_attendance')
        .select('*, gimnasts(id, name, category, level)')
        .eq('club_id', clubId)
        .gte('date', dateFrom)
        .lte('date', dateTo)
        .order('date')
        .execute();
      if (error) throw new Error(error);
      return { data: data || [], error: null };
    } catch (err) {
      console.error('[proxyService] Error en attendanceServiceProxy.getAthleteAttendanceByMonth:', err.message);
      return { data: [], error: err.message };
    }
  },

  async justifyAbsence(attendanceId, justification, rescheduledDate) {
    try {
      const updateData = { status: 'justified', justification };
      if (rescheduledDate) {
        updateData.status = 'rescheduled';
        updateData.rescheduled_date = rescheduledDate;
      }
      const { data, error } = await db('athlete_attendance')
        .update(updateData)
        .eq('id', attendanceId)
        .execute();
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en attendanceServiceProxy.justifyAbsence:', err.message);
      return { data: null, error: err.message };
    }
  },

  async markCoachAttendance(records) {
    try {
      const { data, error } = await db('coach_attendance')
        .upsert(records)
        .execute();
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en attendanceServiceProxy.markCoachAttendance:', err.message);
      return { data: null, error: err.message };
    }
  },

  async markCoachCheckOut(coachId, clubId, date) {
    try {
      const now = new Date().toISOString();
      const { data, error } = await db('coach_attendance')
        .update({ check_out_time: now })
        .eq('coach_id', coachId)
        .eq('club_id', clubId)
        .eq('date', date)
        .execute();
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en attendanceServiceProxy.markCoachCheckOut:', err.message);
      return { data: null, error: err.message };
    }
  },

  async getCoachAttendance(clubId, dateFrom, dateTo) {
    try {
      let query = db('coach_attendance')
        .select('*, coaches(id, name, email, phone, specialties)')
        .eq('club_id', clubId);
      if (dateFrom) query = query.gte('date', dateFrom);
      if (dateTo) query = query.lte('date', dateTo);
      const { data, error } = await query.order('date', { ascending: false }).execute();
      if (error) throw new Error(error);
      return { data: data || [], error: null };
    } catch (err) {
      console.error('[proxyService] Error en attendanceServiceProxy.getCoachAttendance:', err.message);
      return { data: [], error: err.message };
    }
  },

  async getCoachAttendanceByMonth(clubId, year, month) {
    try {
      const dateFrom = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const dateTo = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
      const { data, error } = await db('coach_attendance')
        .select('*, coaches(id, name, email, phone, specialties)')
        .eq('club_id', clubId)
        .gte('date', dateFrom)
        .lte('date', dateTo)
        .order('date')
        .execute();
      if (error) throw new Error(error);
      return { data: data || [], error: null };
    } catch (err) {
      console.error('[proxyService] Error en attendanceServiceProxy.getCoachAttendanceByMonth:', err.message);
      return { data: [], error: err.message };
    }
  },
};

// ============================================================
// STORAGE HELPERS (proxied)
// ============================================================

export const storageServiceProxy = {
  /**
   * Obtiene URL de logo de club
   */
  async getClubLogo(clubId) {
    try {
      const { data, error } = await storageProxy.findFile('gymnastics-images', 'clubs', clubId);
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en storageServiceProxy.getClubLogo:', err.message);
      return { data: null, error: err.message };
    }
  },

  /**
   * Obtiene URL de foto de juez
   */
  async getJudgePhoto(judgeId) {
    try {
      const { data, error } = await storageProxy.findFile('gymnastics-images', 'judges', judgeId);
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en storageServiceProxy.getJudgePhoto:', err.message);
      return { data: null, error: err.message };
    }
  },

  /**
   * Obtiene URL de imagen de evento
   */
  async getEventImage(eventId) {
    try {
      const { data, error } = await storageProxy.findFile('gymnastics-images', 'events', eventId);
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en storageServiceProxy.getEventImage:', err.message);
      return { data: null, error: err.message };
    }
  },

  /**
   * Obtiene URL de foto de coach
   */
  async getCoachPhoto(coachId) {
    try {
      const { data, error } = await storageProxy.findFile('gymnastics-images', 'coaches', coachId);
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en storageServiceProxy.getCoachPhoto:', err.message);
      return { data: null, error: err.message };
    }
  },

  /**
   * Obtiene URL de foto de gimnasta
   */
  async getGymnastPhoto(gymnastId) {
    try {
      const { data, error } = await storageProxy.findFile('gymnastics-images', 'gymnasts', gymnastId);
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en storageServiceProxy.getGymnastPhoto:', err.message);
      return { data: null, error: err.message };
    }
  },

  /**
   * Lista archivos en un bucket/path
   */
  async list(bucket, path = '', limit = 100) {
    return storageProxy.list(bucket, path, limit);
  },

  /**
   * Obtiene URL pública de un archivo
   */
  async getPublicUrl(bucket, filePath) {
    return storageProxy.getPublicUrl(bucket, filePath);
  },

  /**
   * Sube un archivo al storage
   */
  async upload(bucket, filePath, file, options = {}) {
    return storageProxy.upload(bucket, filePath, file, options);
  },

  /**
   * Helper: Sube logo de club
   */
  async uploadClubLogo(clubId, file) {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${clubId}-${Date.now()}.${fileExt}`;
      const filePath = `clubs/${fileName}`;
      
      const result = await storageProxy.upload('gymnastics-images', filePath, file);
      return result;
    } catch (err) {
      console.error('[proxyService] Error en uploadClubLogo:', err.message);
      return { success: false, data: null, error: err.message };
    }
  },

  /**
   * Helper: Sube foto de atleta
   */
  async uploadAthletePhoto(athleteId, file) {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${athleteId}-${Date.now()}.${fileExt}`;
      const filePath = `gymnasts/${fileName}`;
      
      const result = await storageProxy.upload('gymnastics-images', filePath, file);
      return result;
    } catch (err) {
      console.error('[proxyService] Error en uploadAthletePhoto:', err.message);
      return { success: false, data: null, error: err.message };
    }
  },
};

// ============================================================
// CLUB PLANES (FINANZAS)
// ============================================================
export const clubPlanServiceProxy = {
  async getByClub(clubId) {
    try {
      const { data, error } = await db('club_planes')
        .select('*')
        .eq('club_id', clubId)
        .eq('is_active', true)
        .order('name')
        .execute();
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en clubPlanServiceProxy.getByClub:', err.message);
      return { data: null, error: err.message };
    }
  },

  async create(plan) {
    try {
      console.log('[clubPlanServiceProxy] Inserting into club_planes:', plan);
      
      // Use raw supabase client directly to bypass RLS issues
      const { data, error } = await supabase
        .from('club_planes')
        .insert(plan)
        .select()
        .single();
      if (error) {
        console.error('[clubPlanServiceProxy] Insert error:', JSON.stringify(error));
        return { data: null, error: error.message };
      }
      console.log('[clubPlanServiceProxy] Insert result:', data);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en clubPlanServiceProxy.create:', err.message, err.stack);
      return { data: null, error: err.message };
    }
  },

  async update(id, updates) {
    try {
      console.log('[clubPlanServiceProxy] Updating plan:', id, updates);
      // Use raw supabase client directly to bypass RLS issues
      const { data, error } = await supabase
        .from('club_planes')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) {
        console.error('[clubPlanServiceProxy] Update error:', JSON.stringify(error));
        return { data: null, error: error.message };
      }
      console.log('[clubPlanServiceProxy] Update result:', data);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en clubPlanServiceProxy.update:', err.message, err.stack);
      return { data: null, error: err.message };
    }
  },

  async delete(id) {
    try {
      const { error } = await db('club_planes')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', id)
        .execute();
      if (error) throw new Error(error);
      return { success: true, error: null };
    } catch (err) {
      console.error('[proxyService] Error en clubPlanServiceProxy.delete:', err.message);
      return { success: false, error: err.message };
    }
  },
};

// ============================================================
// CLUB MEMBERSHIPS (Membresías)
// ============================================================
export const clubMembershipServiceProxy = {
  async getByClub(clubId) {
    try {
      const { data, error } = await db('club_memberships')
        .select('*, gimnasts(id, name, level, category), club_planes(id, name, price, frequency)')
        .eq('club_id', clubId)
        .order('created_at', { ascending: false })
        .execute();
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en clubMembershipServiceProxy.getByClub:', err.message);
      return { data: null, error: err.message };
    }
  },
  async create(membership) {
    try {
      console.log('[clubMembershipServiceProxy] Inserting:', membership);
      const { data, error } = await supabase
        .from('club_memberships')
        .insert(membership)
        .select()
        .single();
      if (error) {
        console.error('[clubMembershipServiceProxy] Insert error:', JSON.stringify(error));
        return { data: null, error: error.message };
      }
      console.log('[clubMembershipServiceProxy] Insert result:', data);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en clubMembershipServiceProxy.create:', err.message, err.stack);
      return { data: null, error: err.message };
    }
  },

  async assignGymnast(membership) {
    try {
      const { data, error } = await db('club_memberships')
        .insert(membership)
        .select()
        .single();
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en clubMembershipServiceProxy.assignGymnast:', err.message);
      return { data: null, error: err.message };
    }
  },

  // Obtiene la membresía activa de una atleta (última activa si hay varias)
  async getActiveByGymnast(gymnastId) {
    try {
      const { data, error } = await db('club_memberships')
        .select('*, club_planes(id, name, days_per_week, frequency, price)')
        .eq('gymnast_id', gymnastId)
        .eq('status', 'active')
        .order('start_date', { ascending: false })
        .limit(1)
        .execute();
      if (error) throw new Error(error);
      return { data: (data && data[0]) || null, error: null };
    } catch (err) {
      console.error('[proxyService] Error en clubMembershipServiceProxy.getActiveByGymnast:', err.message);
      return { data: null, error: err.message };
    }
  },

  // Upsert: cancela membresía activa existente y crea una nueva con el plan dado.
  // Si planId es null, solo cancela.
  async setActivePlanForGymnast(gymnastId, clubId, planId) {
    try {
      // Cancelar membresías activas previas
      await db('club_memberships')
        .update({ status: 'cancelled', end_date: new Date().toISOString().split('T')[0], updated_at: new Date().toISOString() })
        .eq('gymnast_id', gymnastId)
        .eq('status', 'active')
        .execute();

      if (!planId) return { data: null, error: null };

      // Crear nueva membresía activa
      const { data, error } = await supabase
        .from('club_memberships')
        .insert({
          gymnast_id: gymnastId,
          club_id: clubId,
          plan_id: planId,
          start_date: new Date().toISOString().split('T')[0],
          status: 'active',
        })
        .select()
        .single();
      if (error) return { data: null, error: error.message };
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en clubMembershipServiceProxy.setActivePlanForGymnast:', err.message);
      return { data: null, error: err.message };
    }
  },

  async updateStatus(id, status) {
    try {
      const { data, error } = await db('club_memberships')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en clubMembershipServiceProxy.updateStatus:', err.message);
      return { data: null, error: err.message };
    }
  },

  async cancel(id) {
    try {
      const { data, error } = await db('club_memberships')
        .update({ status: 'cancelled', end_date: new Date().toISOString().split('T')[0], updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en clubMembershipServiceProxy.cancel:', err.message);
      return { data: null, error: err.message };
    }
  },
};

// ============================================================
// CLUB PAGOS
// ============================================================
export const clubPagosServiceProxy = {
  async getByClub(clubId) {
    try {
      const { data, error } = await db('club_pagos')
        .select('*, gimnasts(id, name), club_planes(id, name, price)')
        .eq('club_id', clubId)
        .order('payment_date', { ascending: false })
        .limit(100)
        .execute();
      if (error) throw new Error(error);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en clubPagosServiceProxy.getByClub:', err.message);
      return { data: null, error: err.message };
    }
  },

  async recordPago(pago) {
    try {
      console.log('[clubPagosServiceProxy] Inserting:', pago);
      const { data, error } = await db('club_pagos')
        .insert(pago)
        .select()
        .single();
      if (error) {
        console.error('[clubPagosServiceProxy] Insert error:', JSON.stringify(error));
        return { data: null, error: error.message };
      }
      console.log('[clubPagosServiceProxy] Insert result:', data);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en clubPagosServiceProxy.recordPago:', err.message);
      return { data: null, error: err.message };
    }
  },

  async create(pago) {
    try {
      console.log('[clubPagosServiceProxy] Inserting:', pago);
      const { data, error } = await supabase
        .from('club_pagos')
        .insert(pago)
        .select()
        .single();
      if (error) {
        console.error('[clubPagosServiceProxy] Insert error:', JSON.stringify(error));
        return { data: null, error: error.message };
      }
      console.log('[clubPagosServiceProxy] Insert result:', data);
      return { data, error: null };
    } catch (err) {
      console.error('[proxyService] Error en clubPagosServiceProxy.create:', err.message, err.stack);
      return { data: null, error: err.message };
    }
  },

  async delete(pagoId) {
    try {
      const { error } = await supabase
        .from('club_pagos')
        .delete()
        .eq('id', pagoId);
      if (error) {
        console.error('[clubPagosServiceProxy] Delete error:', JSON.stringify(error));
        return { error: error.message };
      }
      return { error: null };
    } catch (err) {
      console.error('[proxyService] Error en clubPagosServiceProxy.delete:', err.message);
      return { error: err.message };
    }
  },

  async getAlertasDeuda(clubId) {
    try {
      const { data, error } = await db('club_memberships')
        .select('*, gimnasts(id, name, level, category), club_planes(id, name, price, frequency, cut_day, grace_days)')
        .eq('club_id', clubId)
        .eq('status', 'active')
        .execute();
      if (error) throw new Error(error);

      // Cargar pagos del club y mapear el último por membership/gymnast
      const { data: pagosData } = await db('club_pagos')
        .select('membership_id, gymnast_id, payment_date')
        .eq('club_id', clubId)
        .order('payment_date', { ascending: false })
        .execute();
      const lastPaymentByMembership = {};
      const lastPaymentByGymnast = {};
      for (const p of (pagosData || [])) {
        if (p.membership_id && !lastPaymentByMembership[p.membership_id]) {
          lastPaymentByMembership[p.membership_id] = p.payment_date;
        }
        if (p.gymnast_id && !lastPaymentByGymnast[p.gymnast_id]) {
          lastPaymentByGymnast[p.gymnast_id] = p.payment_date;
        }
      }

      const today = new Date();
      const alertas = [];
      
      for (const m of data || []) {
        const plan = m.club_planes;
        const lastPayment = lastPaymentByMembership[m.id] || lastPaymentByGymnast[m.gymnast_id] || null;
        
        let daysSincePayment = 0;
        if (lastPayment) {
          daysSincePayment = Math.floor((today - new Date(lastPayment)) / (1000 * 60 * 60 * 24));
        }

        const frequencyDays = {
          'diario': 1, 'semanal': 7, 'quincenal': 15, 'mensual': 30,
          'trimestral': 90, 'semestral': 180, 'anual': 365
        };
        
        const maxDays = frequencyDays[plan?.frequency] || 30;
        const allowedDays = maxDays + (plan?.grace_days || 3);
        
        if (!lastPayment || daysSincePayment > allowedDays) {
          alertas.push({
            membership_id: m.id,
            gymnast: m.gimnasts,
            plan: plan,
            lastPayment,
            days_since_payment: daysSincePayment,
            is_overdue: !lastPayment || daysSincePayment > maxDays
          });
        }
      }
      
      return { data: alertas, error: null };
    } catch (err) {
      console.error('[proxyService] Error en clubPagosServiceProxy.getAlertasDeuda:', err.message);
      return { data: null, error: err.message };
    }
  },
};

// ============================================================
// JOB POSTINGS SERVICE
// ============================================================
export const jobPostingServiceProxy = {
  async getByClub(clubId) {
    try {
      const { data, error } = await db('job_postings')
        .select('*')
        .eq('club_id', clubId)
        .order('created_at', { ascending: false })
        .execute();
      
      if (error) throw new Error(error);
      return { data: data || [], error: null };
    } catch (err) {
      console.error('[proxyService] Error en getByClub:', err.message);
      return { data: [], error: err.message };
    }
  },

  async getApplicationsByClub(clubId) {
    try {
      // First get job_postings for this club
      const { data: jobData } = await db('job_postings')
        .select('id')
        .eq('club_id', clubId)
        .execute();
      
      const jobIds = jobData?.map(j => j.id) || [];
      
      if (jobIds.length === 0) {
        return { data: [], error: null };
      }
      
      // Then get applications for these jobs
      const { data, error } = await db('job_applications')
        .select('*')
        .in('job_posting_id', jobIds)
        .order('applied_at', { ascending: false })
        .execute();
      
      if (error) throw new Error(error);
      
      // Load related data
      if (data && data.length > 0) {
        for (const app of data) {
          // Load coach
          if (app.coach_id) {
            const { data: coachData } = await db('coaches')
              .select('id, name, email, phone, location, specialties, photo_url, bio, experience_years, auth_user_id')
              .eq('id', app.coach_id)
              .execute();
            if (coachData?.[0]) {
              app.coaches = coachData[0];
            }
          }
          // Load job posting
          if (app.job_posting_id) {
            const { data: jobPostData } = await db('job_postings')
              .select('id, title, club_id')
              .eq('id', app.job_posting_id)
              .execute();
            if (jobPostData?.[0]) {
              app.job_postings = jobPostData[0];
            }
          }
        }
      }
      
      return { data: data || [], error: null };
    } catch (err) {
      console.error('[proxyService] Error en getApplicationsByClub:', err.message);
      return { data: [], error: err.message };
    }
  },

  async getApplicationsByCoach(coachId) {
    try {
      const { data, error } = await db('job_applications')
        .select('*')
        .eq('coach_id', coachId)
        .order('applied_at', { ascending: false })
        .execute();
      
      if (error) throw new Error(error);
      
      // Then load the related data separately
      if (data && data.length > 0) {
        for (const app of data) {
          // Load job posting
          if (app.job_posting_id) {
            const { data: jobData } = await db('job_postings')
              .select('id, title, club_id, disciplines, location, salary_range')
              .eq('id', app.job_posting_id)
              .execute();
            if (jobData?.[0]) {
              app.job_postings = jobData[0];
              // Load club
              if (jobData[0].club_id) {
                const { data: clubData } = await db('clubs')
                  .select('id, name, location, phone, email, facebook_url, instagram_url, twitter_url')
                  .eq('id', jobData[0].club_id)
                  .execute();
                if (clubData?.[0]) {
                  app.clubs = clubData[0];
                }
              }
            }
          }
        }
      }
      
      return { data: data || [], error: null };
    } catch (err) {
      console.error('[proxyService] Error en getApplicationsByCoach:', err.message);
      return { data: [], error: err.message };
    }
  },

  async updateApplicationStatus(applicationId, status) {
    try {
      const { data, error } = await db('job_applications')
        .update({ status, resolved_at: new Date().toISOString() })
        .eq('id', applicationId)
        .select()
        .execute();
      
      if (error) throw new Error(error);
      return { data: data?.[0] || null, error: null };
    } catch (err) {
      console.error('[proxyService] Error en updateApplicationStatus:', err.message);
      return { data: null, error: err.message };
    }
  },

  async deleteApplication(applicationId) {
    try {
      const { error } = await db('job_applications')
        .delete()
        .eq('id', applicationId)
        .execute();
      
      if (error) throw new Error(error);
      return { error: null };
    } catch (err) {
      console.error('[proxyService] Error en deleteApplication:', err.message);
      return { error: err.message };
    }
  },
};
